import { useCallback, useEffect } from 'react';
import { useImmerAtom, useSetImmerAtom } from 'jotai-immer';
import { tblSpecLoadStorage, tblSpecLoadView } from '../../../../core/table/tableViewsRepo';
import {
    tableDataVM,
    tableFiltersVM,
    tableStateVM,
    tableStatsVM,
    tableViewVM,
} from '../../../../core/table/table_view_models';
import {
    PAYOR_LIST_STORAGE_KEY,
    PAYOR_LIST_VIEW_KEY,
    PAYOR_MED_TYPE_MANAGED,
    PAYOR_MED_TYPE_MEDICAID,
} from '../../payor_const';
import {
    tableStateAtom,
    tableViewAtom,
    payorDataAtom,
    tableStatsAtom,
    tableFiltersAtom,
    setAtomPayorListTableViewState,
} from '../payor_list_atoms';
import { payorListColumnSpec, payorListDefaultView } from './PayorListTableSpec';
import viewStateRepo from '../../../../core/repo/viewStateRepo';
import { PayorDataItem } from '../payor_list_types';
import utilSvc from '../../../../core/utilSvc';
import { ListTableInitHookReturn } from '../../../../core/table/table.types';
import apiSvc from '../../../../core/services/apiSvc';
import { GET_LIST } from '../../data/payorRepo';

export const usePayorListTableInit = (): ListTableInitHookReturn => {
    const [{ init }, setState] = useImmerAtom(tableStateAtom);
    const setView = useSetImmerAtom(tableViewAtom);
    const setList = useSetImmerAtom(payorDataAtom);
    const setStats = useSetImmerAtom(tableStatsAtom);
    const setFilter = useSetImmerAtom(tableFiltersAtom);

    const getListAsync = useCallback(async () => {
        try {
            const r = await apiSvc.getEntityList<PayorDataItem>(GET_LIST, {});
            setList(draft => {
                draft.dataItems = payorListVM(r);
                draft.items = [];
            });
        } catch (err: any) {
            // err msg handled upstream
            setList(() => tableDataVM<PayorDataItem>());
        } finally {
            setState(draft => {
                draft.waiting = false;
                draft.init = false;
                return draft;
            });
        }
    }, [setList, setState]);

    // INIT
    useEffect(() => {
        setState(draft => {
            if (draft.init && !draft.waiting) {
                // load custom views from storage
                const colSpec = payorListColumnSpec();
                const defaultView = payorListDefaultView();
                const viewStorage = tblSpecLoadStorage(PAYOR_LIST_VIEW_KEY, colSpec, defaultView);
                draft.views = viewStorage.views;
                draft.currentViewId = viewStorage.current;

                // set current view
                setView(() => tblSpecLoadView(viewStorage.current, viewStorage.views, colSpec) || tableViewVM());

                // viewstate filters and paging
                const viewstate = viewStateRepo.getTable(PAYOR_LIST_STORAGE_KEY, Array.from(colSpec.keys()));
                if (!!viewstate) {
                    setView(draft => {
                        draft.sort = viewstate.sort;
                    });
                    setFilter(() => viewstate.flt);
                    setStats(draft => {
                        draft.paging = viewstate.paging;
                    });
                }

                getListAsync();

                draft.waiting = true;
                return draft;
            } else {
                return draft;
            }
        });

        // destroy
        return (): void => {
            setAtomPayorListTableViewState();
            setState(() => tableStateVM());
            setView(() => tableViewVM());
            setFilter(() => tableFiltersVM());
            setStats(() => tableStatsVM());
            setList(() => tableDataVM<PayorDataItem>());
        };
    }, [setState, setView, getListAsync, setList, setFilter, setStats]);

    return { init };
};

export function payorListVM(data: any): PayorDataItem[] {
    const items: PayorDataItem[] = Array.isArray(data) ? data : [];

    items.forEach(item => {
        item.MedType = item.MedType == 'M' ? PAYOR_MED_TYPE_MEDICAID : PAYOR_MED_TYPE_MANAGED;
        item.CreatedDate = utilSvc.dt.cleanJson(item.CreatedDate);
        item.ModifiedDate = utilSvc.dt.cleanJson(item.ModifiedDate);
    });

    return items;
}
